import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronRight, ChevronDown } from 'lucide-react';
import { useModal } from '../context/ModalContext';

const navItems = [
  { label: 'Home', path: '/' },
  { label: 'About Us', path: '/about' },
  { label: 'Services', path: '/services' },
  { label: 'Careers', path: '/careers' },
  { label: 'Contact', path: '/contact' }
];

const servicesDropdown = [
  { label: 'Vendor Finance', path: '/services?product=vendor-finance' },
  { label: 'Dealer Finance', path: '/services?product=dealer-finance' },
  { label: 'Working Capital Loan', path: '/services?product=working-capital-loan' },
  { label: 'Channel Finance', path: '/services?product=channel-finance' },
  { label: 'Private Credit', path: '/services?product=private-credit' },
  { label: 'Special Situation Funding', path: '/services?product=special-situation-funding' },
  { label: 'Invoice Discounting', path: '/services?product=invoice-discounting' },
  { label: 'Purchase Finance', path: '/services?product=purchase-finance' },
  { label: 'Acquisition Finance', path: '/services?product=acquisition-finance' },
  { label: 'Import Finance', path: '/services?product=import-finance' },
  { label: 'Export Finance', path: '/services?product=export-finance' },
  { label: 'Buyer Credit', path: '/services?product=buyer-credit' }
];

const NavigationExact = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();
  const { openModal } = useModal();

  const handleMobileNav = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm"
      >
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20 lg:h-28">
            {/* Logo */}
            <Link to="/" className="inline-flex items-center justify-center ml-0 sm:ml-0 lg:ml-0 h-full w-auto sm:w-72 lg:w-80 text-slate-900 hover:text-slate-900 visited:text-slate-900 focus:text-slate-900">
                <img
                  src="/company-logo.png"
                  alt="Funding Sathi Logo"
                  className="h-10 sm:h-14 lg:h-18 w-auto"
                />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-10 ml-20">
              <Link
                to="/"
                className={`text-base font-medium transition-colors ${
                  location.pathname === '/' 
                    ? 'text-[#5C1F33]' 
                    : 'text-[#333333] hover:text-[#5C1F33]'
                }`}
              >
                Home
              </Link>
              <Link
                to="/about"
                className={`text-base font-medium transition-colors ${
                  location.pathname === '/about' 
                    ? 'text-[#5C1F33]' 
                    : 'text-[#333333] hover:text-[#5C1F33]'
                }`}
              >
                About Us
              </Link>
              
              {/* Services Dropdown */}
              <div 
                className="relative"
                onMouseEnter={() => setIsServicesOpen(true)}
                onMouseLeave={() => setIsServicesOpen(false)}
              >
                <Link
                  to="/services"
                  className={`text-base font-medium transition-colors flex items-center gap-1 ${
                    location.pathname === '/services' 
                      ? 'text-[#5C1F33]' 
                      : 'text-[#333333] hover:text-[#5C1F33]'
                  }`}
                >
                  Services
                  <ChevronDown size={16} className={`transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
                </Link>
                
                <AnimatePresence>
                  {isServicesOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-100 py-2 z-50"
                    >
                      {servicesDropdown.map((service, index) => (
                        service.type === 'divider' ? (
                          <div key={`divider-${index}`} className="my-2 border-t border-gray-200" />
                        ) : (
                          <Link
                            key={service.label}
                            to={service.path}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#5C1F33] transition-colors"
                          >
                            {service.label}
                          </Link>
                        )
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              
              <Link
                to="/careers"
                className={`text-base font-medium transition-colors ${
                  location.pathname === '/careers' 
                    ? 'text-[#5C1F33]' 
                    : 'text-[#333333] hover:text-[#5C1F33]'
                }`}
              >
                Careers
              </Link>
              <Link
                to="/contact"
                className={`text-base font-medium transition-colors ${
                  location.pathname === '/contact' 
                    ? 'text-[#5C1F33]' 
                    : 'text-[#333333] hover:text-[#5C1F33]'
                }`}
              >
                Contact
              </Link>
            </div>

            {/* CTA Button */}
            <div className="hidden lg:block">
              <button
                type="button"
                onClick={openModal}
                className="inline-flex items-center gap-2 bg-[#5C1F33] text-white px-6 py-3 rounded-md text-base font-medium hover:bg-[#800020] transition-all"
              >
                Apply Now
              </button>
              </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="lg:hidden p-2 rounded-lg text-[#5C1F33] hover:text-[#800020] transition-colors"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50 lg:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed top-0 right-0 bottom-0 w-80 bg-white z-50 lg:hidden shadow-2xl"
            >
              <div className="p-6">
                <div className="flex justify-end mb-8">
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-2 text-[#333333] hover:text-[#5C1F33]"
                  >
                    <X size={24} />
                  </button>
                </div>

                <nav className="space-y-2 mb-8">
                  {navItems.map((item) => (
                    <Link
                      key={item.label}
                      to={item.path}
                      onClick={handleMobileNav}
                      className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition-colors ${
                        location.pathname === item.path
                          ? 'bg-[#5C1F33]/10 text-[#5C1F33]'
                          : 'text-[#333333] hover:bg-gray-50 hover:text-[#5C1F33]'
                      }`}
                    >
                      <span className="font-medium">{item.label}</span>
                      <ChevronRight size={18} />
                    </Link>
                  ))}
                </nav>

                <button
                  type="button"
                  onClick={() => {
                    handleMobileNav();
                    openModal();
                  }}
                  className="w-full flex items-center justify-center gap-2 bg-[#5C1F33] text-white px-5 py-3 rounded-md font-medium hover:bg-[#800020] transition-all"
                >
                  Apply Now
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default NavigationExact;
